import { canTransition as canTransition$1, ALLOWED_TRANSITIONS } from '@levelup/api-contract';
import { ApiError } from '@levelup/api-client';
import { canTransition } from '@levelup/domain';

// src/identity/me.ts
function createMeRepo(api) {
  return {
    get: () => api.identity.getMe({}),
    async switchTenant(targetTenantId) {
      const res = await api.identity.switchActiveTenant({
        targetTenantId
      });
      await api.refreshToken?.();
      return res;
    },
    joinByCode: (tenantCode) => api.identity.joinTenant({ tenantCode }),
    computeActiveRole: (me) => me.claims?.role,
    isPermitted: (me, key) => {
      const perms = me.claims?.permissions;
      return Boolean(perms?.[key]);
    }
  };
}

// src/internal/paginate.ts
async function listPage(fn, req) {
  const res = await fn(req);
  return {
    items: res.items ?? [],
    nextCursor: res.nextCursor ?? null,
    ...res.total !== void 0 ? { total: res.total } : {}
  };
}
async function paginate(fn, req) {
  const page = await listPage(fn, req);
  return toBag(fn, req, page);
}
function toBag(fn, req, page) {
  const bag = {
    items: page.items,
    nextCursor: page.nextCursor,
    ...page.total !== void 0 ? { total: page.total } : {}
  };
  if (page.nextCursor !== null) {
    bag.fetchNextPage = async () => {
      const next = await listPage(fn, { ...req, cursor: page.nextCursor });
      return toBag(fn, req, next);
    };
  }
  return bag;
}
function can(domain, from, to) {
  if (!from) return false;
  return canTransition$1(domain, from, to);
}

// src/identity/tenant.ts
var WRITABLE_STATUSES = /* @__PURE__ */ new Set(["trial", "active"]);
function createTenantRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listTenants, filter),
    paginate: (filter = {}) => paginate(api.identity.listTenants, filter),
    get: (tenantOverride) => api.identity.getTenant(tenantOverride ? { tenantOverride } : {}),
    save: (input) => api.identity.saveTenant(input),
    deactivate: (tenantOverride, reason) => api.identity.deactivateTenant({ tenantOverride, reason }),
    reactivate: (tenantOverride) => api.identity.reactivateTenant({ tenantOverride }),
    exportData: (input) => api.identity.exportTenantData(input),
    uploadAsset: (input) => api.identity.uploadTenantAsset(input),
    lookupByCode: (code) => api.identity.lookupTenantByCode({ tenantCode: code }),
    canTransition: (from, to) => can("tenant", from, to),
    isWritable: (tenant) => tenant.status ? WRITABLE_STATUSES.has(tenant.status) : false,
    computeSeatsRemaining: (tenant) => {
      const max = tenant.subscription?.maxStudents ?? tenant.subscription?.maxUsers;
      const used = tenant.usage?.currentStudents ?? tenant.usage?.currentUsers;
      if (max == null || used == null) return null;
      return Math.max(0, max - used);
    }
  };
}

// src/identity/student.ts
function createStudentRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listStudents, filter),
    paginate: (filter = {}) => paginate(api.identity.listStudents, filter),
    get: (id) => api.identity.getStudent({ id }),
    async getMany(ids) {
      if (ids.length === 0) return [];
      const page = await api.identity.listStudents({ ids });
      return page.items;
    },
    save: (input) => api.identity.saveStudent(input),
    archive: (id) => api.identity.saveStudent({ id, delete: true }),
    canArchive: (student) => can("entityStatus", student.status, "archived")
  };
}

// src/identity/teacher.ts
function createTeacherRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listTeachers, filter),
    paginate: (filter = {}) => paginate(api.identity.listTeachers, filter),
    get: (id) => api.identity.getTeacher({ id }),
    async getMany(ids) {
      if (ids.length === 0) return [];
      const page = await api.identity.listTeachers({ ids });
      return page.items;
    },
    save: (input) => api.identity.saveTeacher(input),
    archive: (id) => api.identity.saveTeacher({ id, delete: true }),
    canArchive: (teacher) => can("entityStatus", teacher.status, "archived")
  };
}

// src/identity/parent.ts
function createParentEntityRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listParents, filter),
    paginate: (filter = {}) => paginate(api.identity.listParents, filter),
    async getMany(ids) {
      if (ids.length === 0) return [];
      const page = await api.identity.listParents({ ids });
      return page.items;
    },
    save: (input) => api.identity.saveParent(input),
    archive: (id) => api.identity.saveParent({ id, delete: true }),
    listByStudent: (studentId) => paginate(api.identity.listParents, { studentId }),
    canArchive: (parent) => can("entityStatus", parent.status, "archived")
  };
}

// src/identity/staff.ts
function createStaffRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listStaff, filter),
    paginate: (filter = {}) => paginate(api.identity.listStaff, filter),
    async getMany(ids) {
      if (ids.length === 0) return [];
      const page = await api.identity.listStaff({ ids });
      return page.items;
    },
    save: (input) => api.identity.saveStaff(input),
    archive: (id) => api.identity.saveStaff({ id, delete: true }),
    canArchive: (staff) => can("entityStatus", staff.status, "archived")
  };
}

// src/identity/academic-session.ts
function createAcademicSessionRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listAcademicSessions, filter),
    paginate: (filter = {}) => paginate(api.identity.listAcademicSessions, filter),
    save: (input) => api.identity.saveAcademicSession(input),
    rollover: (input) => api.identity.rolloverSession(input),
    canArchive: (session) => can("entityStatus", session.status, "archived")
  };
}

// src/identity/org-user.ts
function createOrgUserRepo(api) {
  return {
    create: (input) => api.identity.createOrgUser(input),
    bulkImportStudents: (input) => api.identity.bulkImportStudents(input),
    bulkImportTeachers: (input) => api.identity.bulkImportTeachers(input),
    bulkUpdateStatus: (input) => api.identity.bulkUpdateStatus(input)
  };
}

// src/identity/announcement.ts
function createAnnouncementRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listAnnouncements, filter),
    paginate: (filter = {}) => paginate(api.identity.listAnnouncements, filter),
    save: (input) => api.identity.saveAnnouncement(input),
    archive: (id) => api.identity.saveAnnouncement({ id, delete: true }),
    markRead: (id) => api.identity.markAnnouncementRead({ announcementId: id }),
    canTransition: (from, to) => can("announcement", from, to)
  };
}

// src/identity/notification.ts
var NOOP_HANDLE = { unsubscribe() {
}, id: "noop", active: false };
function createNotificationRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listNotifications, filter),
    paginate: (filter = {}) => paginate(api.identity.listNotifications, filter),
    markRead: (id) => api.identity.markNotificationRead({ mode: "one", notificationId: id }),
    markAllRead: () => api.identity.markNotificationRead({ mode: "all" }),
    getBadge: () => api.identity.getNotificationBadge({}),
    subscribeBadge: (cb) => api.subscribe ? api.subscribe(
      "v1.notification.badge",
      {},
      (payload) => cb(payload)
    ) : NOOP_HANDLE,
    getPreferences: () => api.identity.getNotificationPreferences({}),
    savePreferences: (input) => api.identity.saveNotificationPreferences(input)
  };
}

// src/identity/views/class.ts
function createClassRepo(api) {
  return {
    list: (filter = {}) => paginate(api.identity.listClasses, {
      academicSessionId: filter.academicSessionId,
      status: filter.status,
      cursor: filter.cursor,
      limit: filter.limit
    }),
    get: ({ classId }) => api.identity.getClass({ id: classId }),
    save: (input) => api.identity.saveClass(input),
    archive: (id) => api.identity.saveClass({ id, delete: true }),
    computeRosterCount: (detail) => {
      if (detail.roster?.items) return detail.roster.items.length;
      if (detail.students) return detail.students.length;
      return detail.class?.studentCount ?? detail.class?.studentIds?.length ?? 0;
    },
    canArchive: (klass) => can("entityStatus", klass.status, "archived")
  };
}

// src/identity/views/user-search.ts
function createUserSearchRepo(api) {
  return {
    search: (query, filter = {}) => paginate(api.identity.searchUsers, {
      query,
      cursor: filter.cursor,
      limit: filter.limit
    })
  };
}

// src/identity/index.ts
function createIdentityRepos(api) {
  return {
    meRepo: createMeRepo(api),
    tenantRepo: createTenantRepo(api),
    studentRepo: createStudentRepo(api),
    teacherRepo: createTeacherRepo(api),
    parentRepoEntity: createParentEntityRepo(api),
    staffRepo: createStaffRepo(api),
    academicSessionRepo: createAcademicSessionRepo(api),
    orgUserRepo: createOrgUserRepo(api),
    announcementRepo: createAnnouncementRepo(api),
    notificationRepo: createNotificationRepo(api),
    classRepo: createClassRepo(api),
    userSearchRepo: createUserSearchRepo(api)
  };
}

// src/internal/sensitive-keys.ts
var EDIT_ITEM_SCOPE = "__edit_item__";
function editItemKey(itemId) {
  return [EDIT_ITEM_SCOPE, itemId];
}
function isSensitiveKey(key) {
  return Array.isArray(key) && key[0] === EDIT_ITEM_SCOPE;
}

// src/levelup-content/_kit.ts
async function invokeCallable(invoke, request) {
  return await invoke(request);
}
function toPage(raw) {
  const r = raw ?? {};
  const page = {
    items: Array.isArray(r.items) ? r.items : [],
    nextCursor: r.nextCursor ?? null
  };
  if (typeof r.total === "number") page.total = r.total;
  return page;
}
function makePaginator(invoke, baseReq) {
  const fetch = async (req) => {
    const page = toPage(await invoke(req));
    return {
      ...page,
      async fetchNextPage() {
        if (page.nextCursor === null) return fetch({ ...req, cursor: void 0 });
        return fetch({ ...req, cursor: page.nextCursor });
      }
    };
  };
  return fetch(baseReq);
}
async function batchGetMany(invoke, ids, extra) {
  if (ids.length === 0) return [];
  const req = { ...extra ?? {}, ids: [...ids] };
  const res = toPage(await invoke(req));
  return res.items;
}
function canTransition2(domain, from, to) {
  const table = ALLOWED_TRANSITIONS;
  const edges = table[domain]?.[from];
  return Array.isArray(edges) ? edges.includes(to) : false;
}

// src/levelup-content/space.ts
function createSpaceRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter = {}) => lv["listSpaces"](filter).then((r) => toPage(r)),
    paginate: (filter = {}) => makePaginator((req) => lv["listSpaces"](req), filter),
    // Callable returns `{ space }`; callers/hooks expect the SpaceView itself.
    get: (id) => lv["getSpace"]({ spaceId: id }).then((r) => r.space),
    getMany: (ids) => batchGetMany((req) => lv["listSpaces"](req), ids),
    save: (input) => lv["saveSpace"](input),
    // Contract: saveSpace IS the lifecycle verb — there is no publishSpace/archiveSpace.
    publish: (input) => lv["saveSpace"]({ id: input.id, data: { status: "published" } }),
    archive: (input) => lv["saveSpace"]({ id: input.id, data: { status: "archived" } }),
    canTransition: (from, to) => canTransition2("space", from, to),
    canPublish: (space) => {
      if (!canTransition2("space", space.status ?? "draft", "published")) return false;
      const sp = space.stats?.storyPointCount;
      const items = space.stats?.itemCount;
      if (typeof sp === "number" && sp <= 0) return false;
      if (typeof items === "number" && items <= 0) return false;
      return true;
    },
    isPublished: (space) => space.status === "published",
    computeAverageRating: (space) => space.ratingAggregate?.average ?? 0
  };
}

// src/levelup-content/story-point.ts
var ASSESSMENT_TYPES = /* @__PURE__ */ new Set(["timed_test", "quiz", "practice"]);
function createStoryPointRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter) => invokeCallable(lv["listStoryPoints"], filter).then(
      (r) => toPage(r)
    ),
    get: async (input) => {
      const response = await invokeCallable(
        lv["getStoryPoint"],
        input
      );
      return response.storyPoint;
    },
    save: ({ delete: shouldDelete, ...input }) => invokeCallable(lv["saveStoryPoint"], {
      ...input,
      data: { ...input.data, ...shouldDelete ? { deleted: true } : {} }
    }),
    isAssessment: (sp) => ASSESSMENT_TYPES.has(sp.type ?? "")
  };
}
function createItemRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter) => lv["listItems"](filter).then((r) => toPage(r)),
    paginate: (filter) => makePaginator((req) => lv["listItems"](req), filter),
    // No v1.levelup.getItem — answer-stripped reads go through listItems.
    get: async (input) => {
      let cursor;
      for (; ; ) {
        const page = await lv["listItems"]({
          spaceId: input.spaceId,
          storyPointId: input.storyPointId,
          ...cursor ? { cursor } : {}
        });
        const found = (page.items ?? []).find((i) => i.id === input.itemId);
        if (found) return found;
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
      }
      throw new ApiError({
        code: "NOT_FOUND",
        message: "Item not found",
        meta: { resource: "item", id: input.itemId }
      });
    },
    getForEdit: async (input) => {
      const res = await lv["getItemForEdit"](input);
      return { item: res?.item ?? res, cacheKey: editItemKey(input.itemId) };
    },
    getMany: (ids, scope) => batchGetMany((req) => lv["listItems"](req), ids, scope),
    save: (input) => lv["saveItem"](input),
    saveFromBank: (input) => lv["importFromBank"](input),
    getEvaluationConfig: (input) => invokeCallable(lv["getEvaluationConfig"], input)
  };
}

// src/levelup-content/question-bank.ts
function createQuestionBankRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter = {}) => lv["listQuestionBank"](filter).then((r) => toPage(r)),
    paginate: (filter = {}) => makePaginator((req) => lv["listQuestionBank"](req), filter),
    getMany: (ids) => batchGetMany((req) => lv["listQuestionBank"](req), ids),
    save: (input) => lv["saveQuestionBankItem"](input)
  };
}

// src/levelup-content/rubric-preset.ts
function createRubricPresetRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter = {}) => lv["listRubricPresets"](filter).then((r) => toPage(r)),
    paginate: (filter = {}) => makePaginator((req) => lv["listRubricPresets"](req), filter),
    getMany: (ids) => batchGetMany((req) => lv["listRubricPresets"](req), ids),
    save: (input) => lv["saveRubricPreset"](input),
    resolveEffectiveRubric: ({ item, storyPoint, space, tenantDefault }) => {
      if (item?.rubric != null)
        return { rubric: item.rubric, rubricId: item.rubricId ?? null, source: "item" };
      if (storyPoint?.defaultRubric != null)
        return {
          rubric: storyPoint.defaultRubric,
          rubricId: storyPoint.defaultRubricId ?? null,
          source: "storyPoint"
        };
      if (space?.defaultRubric != null)
        return {
          rubric: space.defaultRubric,
          rubricId: space.defaultRubricId ?? null,
          source: "space"
        };
      if (tenantDefault?.rubric != null)
        return {
          rubric: tenantDefault.rubric,
          rubricId: tenantDefault.rubricId ?? null,
          source: "tenant"
        };
      return { rubric: null, rubricId: null, source: "none" };
    }
  };
}

// src/levelup-content/agent.ts
function createAgentRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter) => lv["listAgents"](filter).then((r) => toPage(r)),
    paginate: (filter) => makePaginator((req) => lv["listAgents"](req), filter),
    getMany: (ids) => batchGetMany((req) => lv["listAgents"](req), ids),
    save: (input) => lv["saveAgent"](input),
    isActive: (agent) => agent.isActive !== false
  };
}

// src/levelup-content/chat.ts
function createChatRepo(api) {
  const lv = api.levelup;
  return {
    listSessions: (filter = {}) => lv["listChatSessions"](filter).then((r) => toPage(r)),
    paginateSessions: (filter = {}) => makePaginator((req) => lv["listChatSessions"](req), filter),
    getSession: (id) => lv["getChatSession"]({ sessionId: id }).then((r) => {
      const o = r ?? {};
      return o.session ?? r;
    }),
    getMany: (ids) => batchGetMany((req) => lv["listChatSessions"](req), ids),
    recordMessage: async (input) => {
      const res = await lv["sendChatMessage"](input);
      return res;
    }
  };
}

// src/levelup-content/conversation.ts
function createConversationRepo(api) {
  const levelup = api.levelup;
  return {
    start: (input) => invokeCallable(levelup["startConversation"], input),
    send: (input) => invokeCallable(levelup["sendConversationTurn"], input),
    finish: (input) => invokeCallable(levelup["finishConversation"], input),
    get: (input) => invokeCallable(levelup["getConversation"], input),
    list: (input = {}) => invokeCallable(levelup["listConversations"], input),
    abandon: (input) => invokeCallable(levelup["abandonConversation"], input)
  };
}

// src/levelup-content/store.ts
function createStoreRepo(api) {
  const lv = api.levelup;
  return {
    listStoreSpaces: (filter = {}) => lv["listStoreSpaces"](filter).then((r) => toPage(r)),
    paginateStoreSpaces: (filter = {}) => makePaginator((req) => lv["listStoreSpaces"](req), filter),
    // Callable returns `{ listing }`; callers expect the listing view itself.
    getStoreSpace: (id) => lv["getStoreSpace"]({ spaceId: id }).then((r) => r.listing),
    listReviews: (filter) => lv["listSpaceReviews"](filter).then((r) => toPage(r)),
    saveReview: (input) => lv["saveSpaceReview"](input),
    recordPurchase: async (input) => await lv["purchaseSpace"](input)
  };
}

// src/levelup-content/version.ts
function createVersionRepo(api) {
  const lv = api.levelup;
  return {
    list: (filter) => lv["listVersions"](filter).then((r) => toPage(r)),
    paginate: (filter) => makePaginator((req) => lv["listVersions"](req), filter)
  };
}

// src/levelup-content/assignment.ts
function createAssignmentRepo(api) {
  const lv = api.levelup;
  return {
    save: (input) => lv["assignContent"](input)
  };
}

// src/levelup-content/ai-generation.ts
function createAiGenerationRepo(api) {
  const lv = api.levelup;
  return {
    getGeneration: (input) => lv["generateContent"](input)
  };
}

// src/views/space-detail-view.ts
function createSpaceDetailViewRepo(api) {
  const lv = api.levelup;
  return {
    get: async ({ spaceId, userId }) => {
      const composite = await lv["getSpaceDetail"]({ spaceId, userId }).then((c) => c).catch(() => null);
      if (composite) {
        return {
          space: composite.space ?? null,
          storyPoints: composite.storyPoints ?? [],
          items: composite.items ?? [],
          myProgress: composite.myProgress ?? null
        };
      }
      const spaceRes = await lv["getSpace"]({ spaceId });
      const space = spaceRes?.space ?? spaceRes;
      const spPage = toPage(await lv["listStoryPoints"]({ spaceId }));
      const storyPointIds = spPage.items.map((s) => s.id).filter((x) => typeof x === "string");
      const items = toPage(await lv["listItems"]({ spaceId, storyPointIds })).items;
      const progressRes = await lv["getSpaceProgress"](
        userId ? { spaceId, userId } : { spaceId }
      ).catch(() => null);
      const myProgress = progressRes && typeof progressRes === "object" && "progress" in progressRes ? progressRes.progress : progressRes;
      return { space, storyPoints: spPage.items, items, myProgress };
    }
  };
}

// src/levelup-content/index.ts
function createLevelupContentRepos(api) {
  return {
    spaceRepo: createSpaceRepo(api),
    storyPointRepo: createStoryPointRepo(api),
    itemRepo: createItemRepo(api),
    questionBankRepo: createQuestionBankRepo(api),
    rubricPresetRepo: createRubricPresetRepo(api),
    agentRepo: createAgentRepo(api),
    chatRepo: createChatRepo(api),
    conversationRepo: createConversationRepo(api),
    storeRepo: createStoreRepo(api),
    versionRepo: createVersionRepo(api),
    assignmentRepo: createAssignmentRepo(api),
    aiGenerationRepo: createAiGenerationRepo(api),
    spaceDetailViewRepo: createSpaceDetailViewRepo(api)
  };
}

// src/testsession-progress/paginate.ts
async function listOnce(fetcher, filter) {
  const page = await fetcher(filter);
  return { items: page.items, nextCursor: page.nextCursor, total: page.total };
}
async function paginate2(fetcher, filter) {
  const page = await fetcher(filter);
  return toBag2(fetcher, filter, page);
}
function toBag2(fetcher, filter, page) {
  const bag = {
    items: page.items,
    nextCursor: page.nextCursor,
    total: page.total
  };
  if (page.nextCursor !== null) {
    bag.fetchNextPage = async () => {
      const next = await fetcher({ ...filter, cursor: page.nextCursor });
      return toBag2(fetcher, filter, next);
    };
  }
  return bag;
}

// src/testsession-progress/story-point-type.ts
function storyPointTypeToSessionType(type) {
  switch (type) {
    case "timed_test":
      return "timed_test";
    case "quiz":
      return "quiz";
    case "practice":
    case "standard":
    default:
      return "practice";
  }
}

// src/testsession-progress/test-session.ts
function toMillis(when) {
  return when instanceof Date ? when.getTime() : new Date(when).getTime();
}
function createTestSessionRepo(api) {
  const list = (filter) => listOnce((req) => api.levelup.listTestSessions(req), filter);
  const repo = {
    recordStart: (input) => api.levelup.startTestSession(input),
    recordSubmit: (input) => api.levelup.submitTestSession(input),
    recordEvaluation: (input) => api.levelup.evaluateAnswer(input),
    get: async (id) => (await api.levelup.getTestSession({ sessionId: id })).session,
    list,
    paginate: (filter) => paginate2((req) => api.levelup.listTestSessions(req), filter),
    canSubmit: (session) => canTransition("testSession", session.status, "completed"),
    isExpired: (session, serverNow) => {
      if (session.serverDeadline == null) return false;
      return toMillis(serverNow) >= toMillis(session.serverDeadline);
    },
    computeRemainingMs: (session, serverNow) => {
      if (session.serverDeadline == null) return Number.POSITIVE_INFINITY;
      return Math.max(0, toMillis(session.serverDeadline) - toMillis(serverNow));
    },
    computeAnsweredCount: (session) => session.answeredQuestions,
    computeProgressPct: (session) => session.totalQuestions > 0 ? Math.round(session.answeredQuestions / session.totalQuestions * 100) : 0,
    computeRuntimeView: (session) => (Array.isArray(session.questionOrder) ? session.questionOrder : []).map((itemId, index) => {
      const view = {
        itemId,
        index,
        visited: session.visitedQuestions[itemId] === true,
        markedForReview: session.markedForReview[itemId] === true
      };
      const section = session.sectionMapping?.[itemId];
      if (section !== void 0) view.section = section;
      return view;
    }),
    computeSectionGroups: (session) => {
      const order = repo.computeRuntimeView(session);
      const groups = /* @__PURE__ */ new Map();
      for (const q of order) {
        const key = q.section ?? "";
        const bucket = groups.get(key);
        if (bucket) bucket.push(q);
        else groups.set(key, [q]);
      }
      return Array.from(groups, ([section, questions]) => ({ section, questions }));
    },
    resolveSessionTypeLabel: (storyPointType) => storyPointTypeToSessionType(storyPointType)
  };
  return repo;
}

// src/testsession-progress/progress.ts
var ATTEMPT_HISTORY_CAP = 20;
function createProgressRepo(api) {
  const repo = {
    getSpace: async (spaceId, userId) => {
      const req = userId === void 0 ? { spaceId } : { spaceId, userId };
      return (await api.levelup.getSpaceProgress(req)).progress;
    },
    getStoryPoint: async (spaceId, storyPointId, userId) => {
      const req = userId === void 0 ? { spaceId, storyPointId } : { spaceId, storyPointId, userId };
      return (await api.levelup.getStoryPointProgress(req)).progress;
    },
    // CD13: pass the request body THROUGH — never inject a score onto the wire.
    recordAttempt: (input) => api.levelup.recordItemAttempt(input),
    computeCompletionPct: ({ completedItems, totalItems }) => {
      if (!totalItems || totalItems <= 0) return 0;
      const pct = Math.round(completedItems / totalItems * 100);
      return Math.max(0, Math.min(100, pct));
    },
    computeOverallScore: (progress) => progress.totalPoints > 0 ? Math.round(progress.pointsEarned / progress.totalPoints * 100) / 100 : 0,
    computeStoryPointSummaries: (progress) => Object.values(progress.storyPoints).map((sp) => ({ ...sp, isComplete: sp.status === "completed" })).sort((a, b) => String(a.storyPointId).localeCompare(String(b.storyPointId))),
    isStoryPointComplete: (sp) => sp.status === "completed",
    resolveNextIncompleteStoryPoint: (progress, storyPointIds) => {
      for (const id of storyPointIds) {
        const sp = progress.storyPoints[id];
        if (!sp || sp.status !== "completed") return id;
      }
      return null;
    },
    computeAttemptHistory: (itemProgress) => {
      const attempts = itemProgress.attempts ?? [];
      return [...attempts].sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp))).slice(0, ATTEMPT_HISTORY_CAP);
    }
  };
  return repo;
}

// src/testsession-progress/insight.ts
function createLearningInsightRepo(api) {
  return {
    list: (filter) => listOnce((req) => api.levelup.listLearningInsights(req), filter),
    paginate: (filter) => paginate2((req) => api.levelup.listLearningInsights(req), filter),
    recordDismissal: (insightId) => api.levelup.dismissInsight({ insightId }),
    computeByPriority: (insights) => {
      var _a;
      const out = {};
      for (const insight of insights) {
        (out[_a = insight.priority] ?? (out[_a] = [])).push(insight);
      }
      return out;
    }
  };
}

// src/testsession-progress/evaluation.ts
function createEvaluationRepo(api) {
  return {
    recordEvaluation: (input) => api.levelup.evaluateAnswer(input),
    // `correctness` is a 0..1 score (StoredEvaluation); fully-correct == 1.
    isCorrect: (evaluation) => evaluation.correctness >= 1
  };
}

// src/views/student-summary.ts
async function studentScopeSummary(api, studentId) {
  const res = await api.analytics.getSummary({ scope: "student", studentId });
  if (res.scope !== "student") {
    throw new Error(`getSummary returned scope '${res.scope}', expected 'student'`);
  }
  return {
    studentSummary: res.studentSummary,
    recentInsights: []
  };
}
function createStudentSummaryRepo(api) {
  return {
    get: (studentId) => studentScopeSummary(api, studentId),
    getMany: async (studentIds) => {
      if (studentIds.length === 0) return [];
      const rows = await Promise.all(
        studentIds.map(async (id) => (await studentScopeSummary(api, id)).studentSummary)
      );
      return rows;
    },
    getClassView: async (classId) => {
      const res = await api.analytics.getSummary({ scope: "class", classId });
      if (res.scope !== "class") {
        throw new Error(`getSummary returned scope '${res.scope}', expected 'class'`);
      }
      return {
        classSummary: res.classSummary,
        members: []
      };
    }
  };
}

// src/testsession-progress/index.ts
function createTestSessionProgressRepos(api) {
  return {
    testSessionRepo: createTestSessionRepo(api),
    progressRepo: createProgressRepo(api),
    learningInsightRepo: createLearningInsightRepo(api),
    evaluationRepo: createEvaluationRepo(api),
    studentSummaryRepo: createStudentSummaryRepo(api)
  };
}

// src/gamification/achievement.ts
function createAchievementRepo(api) {
  return {
    listCatalog: (filter) => listOnce((req) => api.levelup.listAchievements(req), { ...filter }),
    paginateCatalog: (filter) => paginate2((req) => api.levelup.listAchievements(req), { ...filter }),
    listEarned: (opts) => listOnce((req) => api.levelup.listStudentAchievements(req), { ...opts }),
    paginateEarned: (opts) => paginate2((req) => api.levelup.listStudentAchievements(req), { ...opts }),
    markSeen: (input) => api.levelup.markAchievementsSeen(input),
    saveDefinition: (input) => api.levelup.saveAchievementDefinition(input),
    computeByCategory: (catalog) => {
      var _a;
      const out = {};
      for (const a of catalog) {
        (out[_a = a.category] ?? (out[_a] = [])).push(a);
      }
      return out;
    },
    computeUnseenCount: (earned) => {
      let n = 0;
      for (const e of earned) if (!e.seen) n += 1;
      return n;
    }
  };
}

// src/gamification/student-level.ts
function createStudentLevelRepo(api) {
  return {
    get: (userId) => api.levelup.getStudentLevel({ userId }),
    computeProgressToNext: (level) => {
      const denom = level.currentXP + level.xpToNextLevel;
      if (denom <= 0) return 0;
      const frac = level.currentXP / denom;
      return frac < 0 ? 0 : frac > 1 ? 1 : frac;
    }
  };
}

// src/gamification/study-goal.ts
var MS_PER_DAY = 864e5;
function daysUntilEnd(endDate, now) {
  const end = Date.parse(`${endDate}T23:59:59.999Z`);
  if (Number.isNaN(end)) return 0;
  return Math.ceil((end - now.getTime()) / MS_PER_DAY);
}
function createStudyGoalRepo(api) {
  return {
    list: (opts) => listOnce((req) => api.levelup.listStudyGoals(req), { ...opts }),
    paginate: (opts) => paginate2((req) => api.levelup.listStudyGoals(req), { ...opts }),
    save: (input) => api.levelup.saveStudyGoal(input),
    archive: (goal) => api.levelup.saveStudyGoal({
      id: goal.id,
      data: {
        title: goal.title,
        description: goal.description,
        targetType: goal.targetType,
        targetCount: goal.targetCount,
        startDate: goal.startDate,
        endDate: goal.endDate,
        deleted: true
      }
    }),
    computeProgressPct: (goal) => {
      if (goal.targetCount <= 0) return 0;
      const frac = goal.currentCount / goal.targetCount;
      return frac < 0 ? 0 : frac > 1 ? 1 : frac;
    },
    computeDaysRemaining: (goal, now = /* @__PURE__ */ new Date()) => daysUntilEnd(goal.endDate, now),
    isOverdue: (goal, now = /* @__PURE__ */ new Date()) => !goal.completed && daysUntilEnd(goal.endDate, now) < 0,
    canEdit: (goal) => goal.archivedAt === null && !goal.completed
  };
}

// src/gamification/study-session.ts
var MS_PER_DAY2 = 864e5;
function isoDay(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}
function createStudySessionRepo(api) {
  return {
    list: (range) => api.levelup.listStudySessions({ ...range }),
    computeHeatmap: (sessions) => {
      const out = /* @__PURE__ */ new Map();
      if (sessions.length === 0) return out;
      const byDay = /* @__PURE__ */ new Map();
      let minMs = Number.POSITIVE_INFINITY;
      let maxMs = Number.NEGATIVE_INFINITY;
      let peakMinutes = 0;
      for (const s of sessions) {
        byDay.set(s.date, s);
        const ms = Date.parse(`${s.date}T00:00:00.000Z`);
        if (!Number.isNaN(ms)) {
          if (ms < minMs) minMs = ms;
          if (ms > maxMs) maxMs = ms;
        }
        if (s.minutesStudied > peakMinutes) peakMinutes = s.minutesStudied;
      }
      if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) return out;
      for (let ms = minMs; ms <= maxMs; ms += MS_PER_DAY2) {
        const date = isoDay(ms);
        const s = byDay.get(date);
        const minutes = s?.minutesStudied ?? 0;
        out.set(date, {
          date,
          intensity: peakMinutes > 0 ? minutes / peakMinutes : 0,
          minutesStudied: minutes,
          itemsCompleted: s?.itemsCompleted ?? 0,
          pointsEarned: s?.pointsEarned ?? 0
        });
      }
      return out;
    }
  };
}

// src/views/leaderboard.ts
function byScoreDesc(a, b) {
  if (b.score !== a.score) return b.score - a.score;
  return a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0;
}
function denseRank(entries, startOffset) {
  let prevScore = null;
  let rank = startOffset;
  let seen = startOffset;
  return entries.map((e) => {
    seen += 1;
    if (prevScore === null || e.score !== prevScore) {
      rank = seen;
      prevScore = e.score;
    }
    return { ...e, rank };
  });
}
function createLeaderboardRepo(api) {
  return {
    getPage: async (scope, opts) => {
      const filter = {
        scope,
        spaceId: opts?.spaceId,
        storyPointId: opts?.storyPointId,
        cursor: opts?.cursor,
        limit: opts?.limit
      };
      let captured = null;
      const bag = await paginate2(async (req) => {
        const res = await api.levelup.getLeaderboard(req);
        captured = res.callerEntry;
        return {
          items: res.items,
          nextCursor: res.nextCursor,
          total: res.total
        };
      }, filter);
      return { ...bag, callerEntry: captured };
    },
    assignRanks: (entries, startOffset = 0) => denseRank([...entries].sort(byScoreDesc), startOffset),
    mergeLive: (snapshotPage, rtdbPayload) => {
      const merged = /* @__PURE__ */ new Map();
      for (const e of snapshotPage.items) merged.set(e.userId, e);
      for (const e of rtdbPayload.entries) merged.set(e.userId, e);
      const ranked = denseRank([...merged.values()].sort(byScoreDesc), 0);
      const snapCaller = snapshotPage.callerEntry;
      let callerEntry = snapCaller ? merged.get(snapCaller.userId) ?? snapCaller : null;
      if (callerEntry && rtdbPayload.callerRank !== null) {
        callerEntry = { ...callerEntry, rank: rtdbPayload.callerRank };
      }
      return {
        items: ranked,
        nextCursor: snapshotPage.nextCursor,
        total: snapshotPage.total,
        fetchNextPage: snapshotPage.fetchNextPage,
        callerEntry
      };
    }
  };
}

// src/views/gamification-view.ts
function createGamificationViewRepo(api) {
  return {
    getSummary: (userId) => api.levelup.getGamificationSummary({ userId })
  };
}

// src/gamification/index.ts
function createGamificationRepos(api) {
  return {
    achievementRepo: createAchievementRepo(api),
    studentLevelRepo: createStudentLevelRepo(api),
    studyGoalRepo: createStudyGoalRepo(api),
    studySessionRepo: createStudySessionRepo(api),
    leaderboardRepo: createLeaderboardRepo(api),
    gamificationViewRepo: createGamificationViewRepo(api)
  };
}

// src/autograde/paginate.ts
async function listOnce2(fetcher, filter) {
  const page = await fetcher(filter);
  const out = {
    items: page.items ?? [],
    nextCursor: page.nextCursor ?? null
  };
  if (page.total !== void 0) out.total = page.total;
  return out;
}
async function paginate3(fetcher, filter) {
  const page = await listOnce2(fetcher, filter);
  return toBag3(fetcher, filter, page);
}
function toBag3(fetcher, filter, page) {
  const bag = {
    items: page.items,
    nextCursor: page.nextCursor
  };
  if (page.total !== void 0) bag.total = page.total;
  if (page.nextCursor !== null) {
    bag.fetchNextPage = async () => {
      const next = await listOnce2(fetcher, { ...filter, cursor: page.nextCursor });
      return toBag3(fetcher, filter, next);
    };
  }
  return bag;
}

// src/autograde/exam.ts
function rubricCriteriaSum(q) {
  const criteria = q.rubric?.criteria ?? [];
  return criteria.reduce((sum, c) => sum + (c.maxPoints ?? c.points ?? 0), 0);
}
var EXAM_STATUS_FANOUT = [
  "draft",
  "question_paper_uploaded",
  "question_paper_extracted",
  "published",
  "grading",
  "results_released",
  "archived"
];
function createExamRepo(api) {
  const ag = api.autograde;
  const toReq = (filter = {}) => {
    const next = {};
    const f = {};
    if (filter.status) f.status = filter.status;
    if (filter.classId) f.classId = filter.classId;
    if (filter.academicSessionId) f.academicSessionId = filter.academicSessionId;
    if (filter.subject) f.subject = filter.subject;
    if (filter.linkedSpaceId) f.linkedSpaceId = filter.linkedSpaceId;
    if (Object.keys(f).length > 0) next.filter = f;
    return next;
  };
  const listOnceSafe = async (filter = {}) => {
    try {
      return await listOnce2(
        (req) => ag.listExams(req),
        toReq(filter)
      );
    } catch (err) {
      if (filter.status) throw err;
      const pages = await Promise.all(
        EXAM_STATUS_FANOUT.map(
          (status) => listOnce2(
            (req) => ag.listExams(req),
            toReq({ ...filter, status })
          ).catch(() => ({ items: [], nextCursor: null }))
        )
      );
      const seen = /* @__PURE__ */ new Set();
      const items = [];
      for (const page of pages) {
        for (const item of page.items) {
          if (seen.has(item.id)) continue;
          seen.add(item.id);
          items.push(item);
        }
      }
      return { items, nextCursor: null };
    }
  };
  return {
    list: (filter = {}) => listOnceSafe(filter),
    paginate: (filter = {}) => paginate3((req) => ag.listExams(req), toReq(filter)),
    get: (id) => ag.getExam({ id }),
    save: (input) => ag.saveExam(input),
    recordExtraction: (input) => ag.extractQuestions(input),
    releaseResults: (input) => ag.releaseResults({ examId: input.examId, classIds: input.classIds }),
    canTransition: (from, to) => canTransition("exam", from, to),
    canPublish: (exam, questions) => {
      const reasons = [];
      if (!canTransition("exam", exam.status ?? "draft", "published")) {
        reasons.push("exam is not in question_paper_extracted status");
      }
      if (questions.length === 0) {
        reasons.push("exam has no questions");
      }
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        const max = q.maxMarks;
        if (typeof max === "number") {
          const sum = rubricCriteriaSum(q);
          if (sum !== max) {
            reasons.push(`question ${i + 1}: rubric sum ${sum} != maxMarks ${max}`);
          }
        }
      }
      return { ok: reasons.length === 0, reasons };
    },
    // Mirrors the §3.6 `grading → results_released` edge — a pure read of
    // ALLOWED_TRANSITIONS (no wire call). True only when the exam can still
    // transition INTO results_released (i.e. status === 'grading'); an exam
    // already in results_released cannot be released again.
    canReleaseResults: (exam) => canTransition("exam", exam.status ?? "draft", "results_released"),
    computePassRate: (stats) => stats?.passRate ?? 0,
    computeGradedPct: (stats) => {
      const total = stats?.totalSubmissions ?? 0;
      const graded = stats?.gradedSubmissions ?? 0;
      return total > 0 ? Math.round(graded / total * 100) : 0;
    }
  };
}

// src/autograde/exam-question.ts
function createExamQuestionRepo(api) {
  const ag = api.autograde;
  const sum = (q) => (q.rubric?.criteria ?? []).reduce(
    (acc, c) => acc + (c.maxScore ?? c.maxPoints ?? c.points ?? 0),
    0
  );
  return {
    list: async (examId) => (await ag.listQuestions({ examId })).questions,
    saveQuestion: (input) => ag.saveExamQuestion(input),
    recordReExtraction: (examId, questionNumber) => ag.extractQuestions({ examId, mode: "single", questionNumber }),
    computeRubricCriteriaSum: (q) => sum(q),
    isRubricMatchingMaxMarks: (q) => typeof q.maxMarks === "number" ? sum(q) === q.maxMarks : false,
    isAuthoringView: (q) => q.rubric?.modelAnswer !== void 0 || q.rubric?.evaluatorGuidance !== void 0
  };
}
var PHASE_MAP = {
  uploaded: "ingest",
  scouting: "scouting",
  scouting_failed: "failed",
  scouting_complete: "scouting",
  grading: "grading",
  grading_partial: "grading",
  grading_failed: "failed",
  grading_complete: "grading",
  finalization_failed: "failed",
  ready_for_review: "review",
  reviewed: "done",
  manual_review_needed: "review",
  failed: "failed"
};
function createSubmissionRepo(api) {
  const ag = api.autograde;
  return {
    list: (filter) => listOnce2((req) => ag.listSubmissions(req), {
      filter
    }),
    paginate: (filter) => paginate3((req) => ag.listSubmissions(req), {
      filter
    }),
    get: (id) => ag.getSubmission({ id }),
    upload: (input) => ag.uploadAnswerSheets(input),
    canTransition: (from, to) => canTransition("submission", from, to),
    isResultVisible: (sub, role) => sub.resultsReleased === true || role === "teacher",
    computePipelinePhase: (status) => status ? PHASE_MAP[status] ?? "ingest" : "ingest",
    computeProgressPct: (sub) => {
      const total = sub.summary?.totalQuestions ?? 0;
      const graded = sub.summary?.questionsGraded ?? 0;
      return total > 0 ? Math.round(graded / total * 100) : 0;
    }
  };
}

// src/autograde/question-submission.ts
var DEFAULT_THRESHOLDS = {
  confidenceThreshold: 0.7,
  autoApproveThreshold: 0.9
};
function createQuestionSubmissionRepo(api) {
  const ag = api.autograde;
  return {
    list: async (submissionId) => (await ag.listQuestionSubmissions({ submissionId })).questionSubmissions,
    recordManualGrade: (input) => ag.gradeQuestion({
      mode: "manual",
      submissionId: input.submissionId,
      questionId: input.questionId,
      score: input.score,
      feedback: input.feedback
    }),
    recordRetryGrade: (input) => ag.gradeQuestion({
      mode: "retry",
      submissionId: input.submissionId,
      questionIds: input.questionIds
    }),
    recordAiGrade: (input) => ag.gradeQuestion({
      mode: "ai",
      submissionId: input.submissionId,
      questionId: input.questionId
    }),
    computeEffectiveScore: (qs) => qs.manualOverride?.score ?? qs.evaluation?.score ?? null,
    isNeedingReview: (qs) => qs.gradingStatus === "needs_review",
    computeConfidenceBand: (qs, thresholds = DEFAULT_THRESHOLDS) => {
      const c = qs.evaluation?.confidence;
      if (typeof c !== "number") return "low";
      if (c >= thresholds.autoApproveThreshold) return "high";
      if (c >= thresholds.confidenceThreshold) return "mid";
      return "low";
    }
  };
}

// src/autograde/evaluation-settings.ts
function createEvaluationSettingsRepo(api) {
  const ag = api.autograde;
  return {
    list: async (includePublic) => {
      const req = includePublic === void 0 ? {} : { includePublic };
      return (await ag.listEvaluationSettings(req)).settings;
    },
    save: (input) => ag.saveEvaluationSettings(input),
    computeDefaultSettings: (settings) => settings.find((s) => s.isDefault === true) ?? null,
    computeEnabledDimensionIds: (s) => (s.enabledDimensions ?? []).map((d) => d.id).filter((id) => typeof id === "string")
  };
}

// src/autograde/dead-letter.ts
function createDeadLetterRepo(api) {
  const ag = api.autograde;
  const toReq = (filter) => filter === void 0 ? {} : { filter };
  return {
    list: (filter) => listOnce2(
      (req) => ag.listDeadLetter(req),
      toReq(filter)
    ),
    paginate: (filter) => paginate3(
      (req) => ag.listDeadLetter(req),
      toReq(filter)
    ),
    resolve: (input) => ag.resolveDeadLetter({ entryId: input.entryId, method: input.method }),
    isResolved: (entry) => entry.resolvedAt != null
  };
}

// src/views/exam-analytics.ts
function createExamAnalyticsRepo(api) {
  const ag = api.autograde;
  return {
    get: (examId) => ag.getExamAnalytics({ examId }),
    computeRows: (map) => {
      if (!map) return [];
      return Object.entries(map).map(([key, value]) => ({ key, value })).sort((a, b) => a.key.localeCompare(b.key));
    },
    computeGradeDistribution: (map) => {
      if (!map) return [];
      const entries = Object.entries(map);
      const total = entries.reduce((sum, [, count]) => sum + (count ?? 0), 0);
      return entries.map(([grade, count]) => ({
        grade,
        count: count ?? 0,
        pct: total > 0 ? Math.round((count ?? 0) / total * 100) : 0
      })).sort((a, b) => a.grade.localeCompare(b.grade));
    }
  };
}

// src/views/grading-review.ts
function hasReviewComposite(ag) {
  return typeof ag.getGradingReviewBundle === "function";
}
function hasOverviewComposite(ag) {
  return typeof ag.getExamGradingOverview === "function";
}
function effectiveScoreOf(qs) {
  const o = qs;
  return o.manualOverride?.score ?? o.evaluation?.score ?? null;
}
function confidenceBandOf(qs) {
  const c = qs.evaluation?.confidence;
  if (typeof c !== "number") return "low";
  if (c >= 0.9) return "high";
  if (c >= 0.7) return "mid";
  return "low";
}
function shapeBundle(raw) {
  const questionsById = /* @__PURE__ */ new Map();
  for (const q of raw.questions) {
    const id = q.id;
    if (typeof id === "string") questionsById.set(id, q);
  }
  const rows = raw.questionSubmissions.map((qs) => {
    const questionId = qs.questionId;
    const question = typeof questionId === "string" ? questionsById.get(questionId) : void 0;
    const row = {
      questionSubmission: qs,
      effectiveScore: effectiveScoreOf(qs),
      confidenceBand: confidenceBandOf(qs)
    };
    if (question !== void 0) row.question = question;
    return row;
  });
  return { submission: raw.submission, rows };
}
function createGradingReviewRepo(api) {
  const ag = api.autograde;
  return {
    getReviewBundle: async ({ submissionId }) => {
      if (hasReviewComposite(ag)) {
        const raw = await ag.getGradingReviewBundle({ submissionId });
        return shapeBundle(raw);
      }
      const submission = await ag.getSubmission({
        id: submissionId
      });
      const examId = submission.examId;
      const questionSubmissions = (await ag.listQuestionSubmissions({ submissionId })).questionSubmissions;
      const questions = typeof examId === "string" ? (await ag.listQuestions({ examId })).questions : [];
      return shapeBundle({ submission, questionSubmissions, questions });
    },
    getExamGradingOverview: async ({ examId }) => {
      if (hasOverviewComposite(ag)) {
        return ag.getExamGradingOverview({ examId });
      }
      const exam = await ag.getExam({ id: examId });
      const submissions = (await ag.listSubmissions({ filter: { examId } })).items;
      let analytics = null;
      try {
        analytics = await ag.getExamAnalytics({ examId });
      } catch {
        analytics = null;
      }
      return { exam, submissions, analytics };
    }
  };
}

// src/autograde/index.ts
function createAutogradeRepos(api) {
  return {
    examRepo: createExamRepo(api),
    examQuestionRepo: createExamQuestionRepo(api),
    submissionRepo: createSubmissionRepo(api),
    questionSubmissionRepo: createQuestionSubmissionRepo(api),
    evaluationSettingsRepo: createEvaluationSettingsRepo(api),
    deadLetterRepo: createDeadLetterRepo(api),
    examAnalyticsRepo: createExamAnalyticsRepo(api),
    gradingReviewRepo: createGradingReviewRepo(api)
  };
}

// src/analytics/paginate.ts
async function listOnce3(fetcher, filter) {
  const page = await fetcher(filter);
  return { items: page.items, nextCursor: page.nextCursor, total: page.total };
}
async function paginate4(fetcher, filter) {
  const page = await fetcher(filter);
  return toBag4(fetcher, filter, page);
}
function toBag4(fetcher, filter, page) {
  const bag = {
    items: page.items,
    nextCursor: page.nextCursor,
    total: page.total
  };
  if (page.nextCursor !== null) {
    bag.fetchNextPage = async () => {
      const next = await fetcher({ ...filter, cursor: page.nextCursor });
      return toBag4(fetcher, filter, next);
    };
  }
  return bag;
}

// src/analytics/insight.ts
var PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
function createInsightRepo(api) {
  return {
    list: (filter) => listOnce3((req) => api.analytics.listInsights(req), filter),
    paginate: (filter) => paginate4((req) => api.analytics.listInsights(req), filter),
    recordDismiss: (insightId) => api.analytics.dismissInsight({ insightId }),
    computeByPriority: (insights) => {
      const out = { high: [], medium: [], low: [] };
      for (const insight of insights) out[insight.priority].push(insight);
      return out;
    },
    computeTopActionable: (insights, n) => {
      const active = insights.filter((i) => i.dismissedAt == null);
      const sorted = [...active].sort(
        (a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
      );
      return sorted.slice(0, Math.max(0, n));
    }
  };
}

// src/analytics/cost.ts
function flatten(map) {
  return Object.entries(map).map(([key, b]) => ({
    key,
    calls: b.calls,
    inputTokens: b.inputTokens,
    outputTokens: b.outputTokens,
    costUsd: b.costUsd
  }));
}
function createCostRepo(api) {
  return {
    listDaily: async (filter) => {
      const res = await api.analytics.getCostSummary({
        granularity: "daily",
        ...filter.date !== void 0 ? { date: filter.date } : {},
        ...filter.range !== void 0 ? { range: filter.range } : {}
      });
      return res.summaries;
    },
    listMonthly: async (filter) => {
      const res = await api.analytics.getCostSummary({
        granularity: "monthly",
        ...filter.month !== void 0 ? { month: filter.month } : {},
        ...filter.range !== void 0 ? { range: filter.range } : {}
      });
      return res.summaries;
    },
    computeByPurposeRows: (summary) => flatten(summary.byPurpose),
    computeByModelRows: (summary) => flatten(summary.byModel),
    computeBudgetStatus: (summary) => {
      const usedPercent = summary.budgetUsedPercent;
      let band = "unknown";
      if (typeof usedPercent === "number") {
        band = usedPercent >= 100 ? "over" : usedPercent >= 80 ? "warning" : "ok";
      }
      return {
        band,
        ...usedPercent !== void 0 ? { usedPercent } : {},
        ...summary.budgetLimitUsd !== void 0 ? { limitUsd: summary.budgetLimitUsd } : {},
        alertSent: summary.budgetAlertSent === true
      };
    }
  };
}

// src/analytics/report.ts
function createReportRepo(api) {
  return {
    getExamReport: ({ examId, studentId }) => api.analytics.generateReport({
      type: "exam-result",
      examId,
      ...studentId !== void 0 ? { studentId } : {}
    }),
    getProgressReport: ({ studentId }) => api.analytics.generateReport({ type: "progress", studentId }),
    getClassReport: ({ classId }) => api.analytics.generateReport({ type: "class", classId })
  };
}

// src/analytics/views/summary.ts
var AT_RISK_COPY = {
  low_exam_score: "Low exam scores",
  no_recent_activity: "No recent activity",
  low_space_completion: "Low course completion",
  declining_performance: "Declining performance",
  zero_streak: "Inactive streak"
};
function createSummaryRepo(api) {
  return {
    getStudent: async (studentId) => {
      const res = await api.analytics.getSummary({ scope: "student", studentId });
      if (res.scope !== "student") {
        throw new Error(`getSummary returned scope '${res.scope}', expected 'student'`);
      }
      return res.studentSummary;
    },
    getClass: async (classId) => {
      const res = await api.analytics.getSummary({ scope: "class", classId });
      if (res.scope !== "class") {
        throw new Error(`getSummary returned scope '${res.scope}', expected 'class'`);
      }
      return res.classSummary;
    },
    getPlatform: async () => {
      const res = await api.analytics.getSummary({ scope: "platform" });
      if (res.scope !== "platform") {
        throw new Error(`getSummary returned scope '${res.scope}', expected 'platform'`);
      }
      return res.platformSummary;
    },
    getHealth: async () => {
      const res = await api.analytics.getSummary({ scope: "health" });
      if (res.scope !== "health") {
        throw new Error(`getSummary returned scope '${res.scope}', expected 'health'`);
      }
      return res.healthSummary;
    },
    computeOverallBand: ({ overallScore }) => {
      if (overallScore >= 0.85) return "excellent";
      if (overallScore >= 0.65) return "good";
      if (overallScore >= 0.4) return "fair";
      return "needs_attention";
    },
    computeAtRiskBadges: ({ atRiskReasons }) => atRiskReasons.map((reason) => ({ reason, label: AT_RISK_COPY[reason] })),
    computeSubjectRows: (summary) => {
      const rows = /* @__PURE__ */ new Map();
      for (const [subject, b] of Object.entries(summary.autograde.subjectBreakdown)) {
        rows.set(subject, {
          subject,
          examAvgScore: b.avgScore,
          examCount: b.examCount
        });
      }
      for (const [subject, completion] of Object.entries(summary.levelup.subjectBreakdown)) {
        const existing = rows.get(subject);
        if (existing) existing.levelupCompletion = completion;
        else rows.set(subject, { subject, levelupCompletion: completion });
      }
      return [...rows.values()];
    }
  };
}

// src/analytics/views/exam-analytics.ts
function toQuestionRow(entry) {
  const row = {
    questionId: entry.questionId,
    avgScore: entry.avgScore,
    maxScore: entry.maxScore,
    attemptCount: entry.attemptCount
  };
  if (entry.difficultyIndex !== void 0) row.difficultyIndex = entry.difficultyIndex;
  if (entry.discriminationIndex !== void 0) row.discriminationIndex = entry.discriminationIndex;
  return row;
}
function toClassRow(entry) {
  return {
    classId: entry.classId,
    avgScore: entry.avgScore,
    avgPercentage: entry.avgPercentage,
    submissionCount: entry.submissionCount
  };
}
function createExamAnalyticsRepo2(api) {
  return {
    get: (examId) => api.analytics.getExamAnalytics({ examId }),
    computeQuestionRows: (analytics) => Object.values(analytics.questionAnalytics).map(toQuestionRow).sort((a, b) => (a.difficultyIndex ?? Infinity) - (b.difficultyIndex ?? Infinity)),
    computeClassRows: (analytics) => Object.values(analytics.classBreakdown).map(toClassRow).sort((a, b) => b.avgPercentage - a.avgPercentage),
    computeDistributionBuckets: (analytics) => analytics.scoreDistribution.buckets.map((b) => ({ label: b.label, count: b.count }))
  };
}

// src/analytics/views/trends.ts
function createTrendsRepo(api) {
  return {
    get: async (filter) => {
      const res = await api.analytics.getPerformanceTrends(filter);
      return res.points;
    },
    computeChartSeries: (points) => points.map((p) => ({
      periodStart: p.periodStart,
      avgPercentage: p.avgPercentage,
      completionPct: p.completionPct,
      overallScore: p.overallScore,
      examCount: p.examCount
    }))
  };
}

// src/analytics/views/parent.ts
function createParentRepo(api) {
  return {
    // ONE batched read for K linked children — no per-child fan-out (PC-14).
    listChildren: (filter) => listOnce3((req) => api.analytics.listLinkedChildren(req), filter ?? {}),
    paginate: (filter) => paginate4((req) => api.analytics.listLinkedChildren(req), filter ?? {}),
    getChildSummary: (studentId) => api.analytics.getChildSummary({ studentId }),
    computeChildCards: (rows) => rows.map((r) => ({
      studentId: r.studentId,
      name: r.name,
      classLine: r.classNames.join(", "),
      overallScore: r.overallScore,
      isAtRisk: r.isAtRisk
    }))
  };
}

// src/analytics/views/leaderboard.ts
function createLeaderboardRepo2(api) {
  return {
    get: (filter) => api.analytics.getLeaderboard(filter),
    getLive: (params, cb) => {
      if (!api.subscribe) {
        return { unsubscribe: () => void 0, id: "noop", active: false };
      }
      return api.subscribe(
        "v1.analytics.leaderboard",
        params,
        (payload) => cb(payload)
      );
    },
    computeRankWithTier: (entries) => {
      const sorted = [...entries].sort((a, b) => a.rank - b.rank);
      return sorted.map((entry, i) => ({
        entry,
        rank: entry.rank,
        gapToAbove: i === 0 ? 0 : sorted[i - 1].score - entry.score
      }));
    }
  };
}

// src/analytics/views/space-analytics.ts
function createSpaceAnalyticsRepo(api) {
  return {
    get: (spaceId) => api.analytics.getSpaceAnalytics({
      spaceId
    })
  };
}

// src/analytics/index.ts
function createAnalyticsRepos(api) {
  return {
    insightRepo: createInsightRepo(api),
    costRepo: createCostRepo(api),
    reportRepo: createReportRepo(api),
    summaryRepo: createSummaryRepo(api),
    examAnalyticsRepo: createExamAnalyticsRepo2(api),
    trendsRepo: createTrendsRepo(api),
    parentRepo: createParentRepo(api),
    leaderboardRepo: createLeaderboardRepo2(api),
    spaceAnalyticsRepo: createSpaceAnalyticsRepo(api)
  };
}

// src/views-and-storage-auth/storage.ts
function createStorageRepo(api) {
  return {
    requestUploadUrl: (input) => api.storage.requestUploadUrl(input),
    async uploadImage(input) {
      const { body, ...signing } = input;
      const grant = await api.storage.requestUploadUrl(signing);
      await api.storage.uploadImage({
        uploadUrl: grant.uploadUrl,
        bytes: body,
        contentType: signing.contentType
      });
      return grant.path;
    },
    resolveStoragePath: (grant) => grant.path,
    canUpload: (input) => input.contentType.trim().length > 0
  };
}

// src/views-and-storage-auth/auth.ts
function createAuthRepo(api) {
  return {
    signIn: (input) => api.auth.signIn(input),
    signOut: () => api.auth.signOut(),
    sendPasswordReset: (email) => api.auth.sendPasswordReset(email),
    restoreSession: () => api.auth.restoreSession(),
    subscribeAuthState: (cb) => api.auth.onAuthState(cb),
    isSignedIn: (session) => Boolean(session?.uid)
  };
}

// src/views-and-storage-auth/device.ts
function createDeviceRepo(api) {
  return {
    register: (input) => api.identity.registerDeviceToken(input),
    unregister: (input) => api.identity.unregisterDeviceToken(input),
    canRegister: (input) => input.token.trim().length > 0
  };
}

// src/views-and-storage-auth/me-asset.ts
function createMeAssetRepo(api) {
  return {
    uploadAvatar: (input) => {
      const req = {
        kind: "avatar",
        contentType: input.contentType,
        bytesBase64: input.bytesBase64
      };
      return api.identity.uploadUserAsset(req);
    },
    canUploadAvatar: (input) => input.contentType.trim().length > 0 && input.bytesBase64.length > 0
  };
}

// src/views-and-storage-auth/index.ts
function createViewsStorageAuthRepos(api) {
  return {
    storageRepo: createStorageRepo(api),
    authRepo: createAuthRepo(api),
    deviceRepo: createDeviceRepo(api),
    meAssetRepo: createMeAssetRepo(api)
  };
}

// src/index.ts
function createRepositories(api) {
  const shared = api;
  const bag = {
    ...createIdentityRepos(shared),
    ...createLevelupContentRepos(shared),
    ...createTestSessionProgressRepos(shared),
    ...createGamificationRepos(shared),
    ...createAutogradeRepos(shared),
    // analytics last: its `examAnalyticsRepo` is the canonical view repo
    // (analytics.md §examAnalyticsRepo) and wins the bag key over autograde's.
    ...createAnalyticsRepos(shared),
    // views-and-storage-auth: cross-cutting transport-seam repos (storage/auth/
    // device/avatar) — distinct bag keys, no collision with the per-entity repos.
    ...createViewsStorageAuthRepos(shared)
  };
  return bag;
}

export { EDIT_ITEM_SCOPE, createAnalyticsRepos, createAutogradeRepos, createIdentityRepos, createLevelupContentRepos, createRepositories, createViewsStorageAuthRepos, editItemKey, isSensitiveKey };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map